import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getProjectMembers, updateProjectMember, removeProjectMember } from "../../../../api/project";
import { RoleMemberProject } from "../../../../constants/user";
import { Button } from "../../../../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../../../../components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../../components/ui/select";
import { Avatar, AvatarFallback } from "../../../../components/ui/avatar";
import { Badge } from "../../../../components/ui/badge";
import { Loader2, Settings, UserPlus, X, Save, Trash, Users } from "lucide-react";
import toastifyInstance from "../../../../utils/toastify";
import { UserInterface } from "../../../../models/user.model";
import AlertComponent from "../../../../components/alert";

interface CurrentMembersTabProps {
    projectId: string;
    onAddMembers: () => void;
}

interface MemberEditState {
    role: RoleMemberProject;
    status: boolean;
}

interface EditingMembersState {
    [memberId: string]: MemberEditState;
}

const CurrentMembersTab: React.FC<CurrentMembersTabProps> = ({ projectId, onAddMembers }) => {
    const queryClient = useQueryClient();

    const [openAlert, setOpenAlert] = useState(false);

    // Get existing members
    const { data: projectMembers, isLoading: isLoadingMembers } = useQuery({
        queryKey: ["project-members", projectId],
        queryFn: () => getProjectMembers(projectId, 'all'),
    });

    // For member editing
    const [editingMembers, setEditingMembers] = useState<EditingMembersState>({});

    // Mutation for updating member
    const updateMemberMutation = useMutation({
        mutationFn: (payload: {
            projectMemberId: string;
            status: boolean;
            role: string;
        }) => updateProjectMember(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["projects"] });
            queryClient.invalidateQueries({ queryKey: ["project-members", projectId] });
            toastifyInstance.success("Member updated successfully.");
            setEditingMembers({});
        },
        onError: (error) => {
            console.error(error);
            toastifyInstance.error("Failed to update member.");
        },
    });

    const removeMemberMutation = useMutation({
        mutationFn: (projectMemberId: string) => removeProjectMember(projectMemberId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["projects"] });
            queryClient.invalidateQueries({ queryKey: ["project-members", projectId] });
            toastifyInstance.success("Member removed successfully.");
        },
        onError: (error) => {
            console.error(error);
            toastifyInstance.error("Failed to remove member.");
        },
    });

    // Start editing a member
    const handleEditMember = (member: UserInterface & { projectMemberRole: RoleMemberProject, projectMemberId: string, projectMemberStatus: boolean }) => {
        setEditingMembers({
            ...editingMembers,
            [member._id]: {
                role: member.role as unknown as RoleMemberProject,
                status: member.projectMemberStatus !== false
            }
        });
    };

    // Cancel editing
    const handleCancelEdit = (memberId: string) => {
        const updatedEditing = { ...editingMembers };
        delete updatedEditing[memberId];
        setEditingMembers(updatedEditing);
    };

    // Update field while editing
    const handleFieldUpdate = (memberId: string, field: "role" | "status", value: RoleMemberProject | boolean) => {
        setEditingMembers({
            ...editingMembers,
            [memberId]: {
                ...editingMembers[memberId],
                [field]: value
            }
        });
    };

    // Save member updates
    const handleSaveMember = (memberId: string, projectMemberId: string) => {
        updateMemberMutation.mutate({
            projectMemberId: projectMemberId,
            role: editingMembers[memberId].role,
            status: editingMembers[memberId].status
        });
    };

    // Get initials for avatar
    const getInitials = (name: string): string => {
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2);
    };

    const handleRemoveUser = (memberId: string) => {
        removeMemberMutation.mutate(memberId);
    };

    return (
        <Card>
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0 pb-2">
                <div className="space-y-1">
                    <CardTitle className="text-lg sm:text-xl font-bold">Project Team Members</CardTitle>
                    <CardDescription>
                        Manage team member roles and status for this project
                    </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                    <Badge variant="outline" className="ml-2 text-xs sm:text-sm">
                        {projectMembers?.length || 0} members
                    </Badge>
                    <Button variant="outline" size="sm" onClick={onAddMembers} className="text-xs sm:text-sm">
                        <UserPlus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        Add Members
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {isLoadingMembers ? (
                    <div className="flex justify-center items-center py-6 sm:py-8">
                        <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : projectMembers?.length > 0 ? (
                    <div className="grid gap-3 sm:gap-4">
                        {projectMembers.map((member: UserInterface & { projectMemberRole: RoleMemberProject, projectMemberId: string, projectMemberStatus: boolean }) => (
                            <Card key={member._id} className="p-3 sm:p-4">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
                                            <AvatarFallback>
                                                {getInitials(member.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="font-medium text-sm sm:text-base">{member.name}</p>
                                            <p className="text-xs sm:text-sm text-muted-foreground">
                                                {member.email}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        {editingMembers[member._id] ? (
                                            <div className="flex items-center gap-2">
                                                <Select
                                                    value={editingMembers[member._id].role}
                                                    onValueChange={(value) =>
                                                        handleFieldUpdate(member._id, "role", value as RoleMemberProject)
                                                    }
                                                >
                                                    <SelectTrigger className="w-28 sm:w-32 text-sm">
                                                        <SelectValue placeholder="Select role" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {Object.values(RoleMemberProject).map((role) => (
                                                            <SelectItem key={role} value={role}>
                                                                {role}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Select
                                                    value={editingMembers[member._id].status ? "active" : "inactive"}
                                                    onValueChange={(value) =>
                                                        handleFieldUpdate(member._id, "status", value === "active")
                                                    }
                                                >
                                                    <SelectTrigger className="w-24 sm:w-28 text-sm">
                                                        <SelectValue placeholder="Status" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="active">Active</SelectItem>
                                                        <SelectItem value="inactive">Inactive</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    onClick={() => handleSaveMember(member._id, member.projectMemberId)}
                                                    disabled={updateMemberMutation.isPending}
                                                >
                                                    <Save className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleCancelEdit(member._id)}
                                                    disabled={updateMemberMutation.isPending}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                                <AlertComponent
                                                    title="Remove member"
                                                    description="Are you sure you want to remove this member?"
                                                    showButton={
                                                        <Button className="text-red-500" variant="outline" size="icon" onClick={() => setOpenAlert(true)}>
                                                            <Trash className="h-4 w-4" />
                                                        </Button>
                                                    }
                                                    open={openAlert}
                                                >
                                                    <Button className="text-red-500" variant="outline" size="icon" onClick={() => handleRemoveUser(member.projectMemberId)}>
                                                        <Trash className="h-4 w-4" />
                                                    </Button>
                                                    <Button className="w-[max-content] px-2 text-xs sm:text-sm" variant="outline" size="icon" onClick={() => setOpenAlert(false)}>
                                                        Cancel
                                                    </Button>
                                                </AlertComponent>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <Badge variant="default" className="text-xs sm:text-sm">
                                                    {member.projectMemberRole}
                                                </Badge>
                                                <Badge variant={member?.projectMemberStatus !== false ? "outline" : "secondary"} className="text-xs sm:text-sm">
                                                    {member?.projectMemberStatus ? "Active" : "Inactive"}
                                                </Badge>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleEditMember(member)}
                                                >
                                                    <Settings className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-6 sm:py-8 text-center">
                        <Users className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground mb-2" />
                        <p className="text-xs sm:text-sm text-muted-foreground">No members found</p>
                        <Button variant="outline" size="sm" className="mt-4 text-xs sm:text-sm" onClick={onAddMembers}>
                            <UserPlus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                            Add Members
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default CurrentMembersTab;